import {
  ArrowDown,
  ArrowUp,
  Mail,
  MoreVertical,
  Phone,
  Trash2,
  UserPlus,
} from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

import { useDeleteWaitList } from '~entities/classes/api/wait-list/delete-wait-list';
import { type WaitListedStudent } from '~entities/classes/api/wait-list/get-wait-list-summary';
import { useUpdatePositionMutation } from '~entities/classes/api/wait-list/update-position';
import { type ClassDetails } from '~entities/classes/index';

import { EnrollStudentModal } from '~features/students/ui/student-payment/enroll-students';
import { useCurrentUserRole } from '~entities/users/hooks/use-current-user-role';

import { Button } from '~shared/shadcn/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '~shared/shadcn/ui/dropdown-menu';

interface WaitListCardProps {
  student: WaitListedStudent;
  classData?: ClassDetails;
  isLastPosition?: boolean;
}

export const WaitListCard = ({
  student,
  classData,
  isLastPosition,
}: WaitListCardProps) => {
  const deleteWaitList = useDeleteWaitList();
  const updatePosition = useUpdatePositionMutation();
  const [enrollStudentDialogOpen, setEnrollStudentDialogOpen] = useState(false);
  const { hasAdminAccess } = useCurrentUserRole();
  const handleDelete = async () => {
    try {
      await deleteWaitList.mutateAsync({
        waitlistId: student.waitlistId,
        classId: classData?._id ?? '',
      });
      toast.success('Student removed from waitlist');
    } catch (error) {
      console.error(error);
      toast.error('Failed to remove student from waitlist');
    }
  };

  const handleMoveUp = async () => {
    try {
      await updatePosition.mutateAsync({
        waitlistId: student.waitlistId,
        newPosition: student.position - 1,
        classId: classData?._id ?? '',
      });
      toast.success('Student moved up');
    } catch (error) {
      console.error(error);
      toast.error('Failed to move student up');
    }
  };

  const handleMoveDown = async () => {
    try {
      await updatePosition.mutateAsync({
        waitlistId: student.waitlistId,
        newPosition: student.position + 1,
        classId: classData?._id ?? '',
      });
      toast.success('Student moved down');
    } catch (error) {
      console.error(error);
      toast.error('Failed to move student down');
    }
  };

  return (
    <>
      <div
        key={student.position}
        className='rounded-2xl border bg-primary/10 p-4 backdrop-blur-sm transition-all duration-200 hover:bg-primary/30'
      >
        <div className='flex items-start space-x-6'>
          <div className='flex h-full items-center justify-center'>
            <div className='flex size-8 shrink-0 items-center justify-center rounded-full bg-secondary'>
              <span className='text-xs font-bold text-white'>
                #{student.position}
              </span>
            </div>
          </div>

          {/* Main Info Block */}
          <div className='min-w-0 flex-1'>
            <div className='flex flex-col space-y-2'>
              <div className='flex items-center space-x-2'>
                <h3 className='truncate text-lg font-bold text-gray-800'>
                  {student.studentName}
                </h3>
                <span className='text-sm text-gray-600'>
                  Age {student.age} •{' '}
                  {student.gender.charAt(0).toUpperCase() +
                    student.gender.slice(1)}
                </span>
              </div>
              <div className='flex items-center space-x-4 text-sm text-gray-600'>
                <span>Parent: {student.parentName}</span>
                <div className='flex items-center space-x-1'>
                  <Phone className='size-3' />
                  <span>{student.parentPhone}</span>
                </div>
                <span className='text-xs text-gray-500'>|</span>
                <div className='flex items-center space-x-1'>
                  <Mail className='size-3' />
                  <span>{student.parentEmail}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Right: Status, Date, Actions */}
          <div className='flex shrink-0 flex-col items-end space-y-3'>
            <div className='flex items-center space-x-3'>
              <span className='text-xs'>
                Joined Waitlist: {student.joinedAt.split('T')[0]}
              </span>
            </div>

            {hasAdminAccess && (<div className='flex items-center space-x-2'>
              <Button
                // onClick={() => {
                //   handleEnroll(student.position);
                // }}
                size='sm'
                variant={'secondary'}
                className='rounded-full hover:bg-secondary'
                onClick={() => {
                  setEnrollStudentDialogOpen(true);
                }}
              >
                <UserPlus className='mr-1 size-3' />
                Enroll
              </Button>

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    size='icon'
                    variant='ghost'
                    className='size-8 hover:bg-transparent'
                  >
                    <MoreVertical className='size-4' />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  align='end'
                  className='w-40 border-0 bg-white/95 shadow-lg backdrop-blur-sm'
                >
                  <DropdownMenuItem
                    onClick={handleMoveUp}
                    disabled={student.position === 1}
                    className='rounded-lg'
                  >
                    <ArrowUp className='mr-2 size-4' />
                    Move Up
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={handleMoveDown}
                    disabled={isLastPosition}
                    className='rounded-lg'
                  >
                    <ArrowDown className='mr-2 size-4' />
                    Move Down
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={handleDelete}
                    className='rounded-lg text-red-600 focus:text-red-600'
                  >
                    <Trash2 className='mr-2 size-4' />
                    Remove
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>)}
          </div>
        </div>
      </div>
      {enrollStudentDialogOpen && (
        <EnrollStudentModal
          dialogOpen={enrollStudentDialogOpen}
          setDialogOpen={setEnrollStudentDialogOpen}
          studentId={student.studentId}
          parentId={student.parentId}
          preSelectedSession={
            classData?.session
              ? {
                  label: classData.session.name,
                  value: classData.session._id,
                }
              : null
          }
          preSelectedClass={
            classData
              ? {
                  label: classData.title,
                  value: classData._id,
                }
              : null
          }
        />
      )}
    </>
  );
};
